import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  Box,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Avatar,
  Container,
  Typography,
  useScrollTrigger,
  Slide,
  Badge,
  Tooltip,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import { motion, AnimatePresence } from "framer-motion";

const navLinks = [
  { title: "Home", path: "/" },
  { title: "About", path: "/about" },
  { title: "Vision", path: "/vision" },
  { title: "Media", path: "/media" },
  { title: "Insights", path: "/insights" },
  { title: "Contact", path: "/contact" },
];

// Hide navbar on scroll down, show on scroll up
function HideOnScroll({ children }) {
  const trigger = useScrollTrigger();
  return (
    <Slide appear={false} direction="down" in={!trigger}>
      {children}
    </Slide>
  );
}

const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [hoveredLink, setHoveredLink] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  const drawer = (
    <Box sx={{ textAlign: "center", height: "100%", position: "relative" }}>
      {/* Close Button */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", p: 2 }}>
        <IconButton onClick={handleDrawerToggle} sx={{ color: "#fff" }}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Logo in Drawer */}
      <Box sx={{ display: "flex", justifyContent: "center", mb: 4 }}>
        <motion.div
          initial={{ scale: 0, rotate: -180 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ duration: 0.6, type: "spring" }}
        >
          <Link to="/" onClick={handleDrawerToggle}>
            <Avatar
              src="/images/logo.png"
              alt="John Asfar Logo"
              sx={{
                width: 100,
                height: 100,
                border: "2px solid #C8A96B",
                boxShadow: "0 0 30px rgba(200,169,107,0.4)",
                transition: "all 0.3s ease",
                "&:hover": {
                  transform: "scale(1.05)",
                  boxShadow: "0 0 40px rgba(200,169,107,0.6)",
                },
              }}
            />
          </Link>
        </motion.div>
      </Box>

      {/* Navigation Links in Drawer */}
      <List sx={{ mb: 4 }}>
        {navLinks.map((link, index) => (
          <motion.div
            key={link.title}
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.1 }}
          >
            <ListItem disablePadding>
              <ListItemButton
                component={Link}
                to={link.path}
                onClick={handleDrawerToggle}
                sx={{
                  justifyContent: "center",
                  textAlign: "center",
                  py: 1.5,
                  mx: 2,
                  borderRadius: 2,
                  position: "relative",
                  background: isActive(link.path) 
                    ? "linear-gradient(90deg, rgba(200,169,107,0.1), transparent)"
                    : "transparent",
                  "&::before": isActive(link.path)
                    ? {
                        content: '""',
                        position: "absolute",
                        left: 0,
                        top: "50%",
                        transform: "translateY(-50%)",
                        width: "3px",
                        height: "30px",
                        background: "#C8A96B",
                        borderRadius: "0 2px 2px 0",
                      }
                    : {},
                  "&:hover": {
                    background: "rgba(200,169,107,0.1)",
                    transform: "translateX(5px)",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                <ListItemText
                  primary={link.title}
                  primaryTypographyProps={{
                    sx: {
                      color: isActive(link.path) ? "#C8A96B" : "#fff",
                      fontSize: "1.1rem",
                      fontWeight: isActive(link.path) ? 600 : 400,
                      textAlign: "center",
                    },
                  }}
                />
              </ListItemButton>
            </ListItem>
          </motion.div>
        ))}
      </List>

      {/* Contact Info in Mobile Menu Footer */}
      <Box
        sx={{
          position: "absolute",
          bottom: 30,
          left: 0,
          right: 0,
          textAlign: "center",
          p: 2,
        }}
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
        >
          <Typography variant="caption" sx={{ color: "#666", display: "block", mb: 1 }}>
            Direct Access Line
          </Typography>
          <Typography 
            variant="body2" 
            sx={{ 
              color: "#C8A96B", 
              fontWeight: 600,
              letterSpacing: 1,
            }}
          >
            +27 72 010 0332
          </Typography>
          <Typography variant="caption" sx={{ color: "#666", display: "block", mt: 1 }}>
            John.Asfar@JohnAsfar.com
          </Typography>
        </motion.div>
      </Box>
    </Box>
  );

  return (
    <>
      <HideOnScroll>
        <AppBar
          position="fixed"
          elevation={0}
          sx={{
            background: scrolled
              ? "rgba(11,11,11,0.98)"
              : "rgba(0,0,0,0.8)",
            backdropFilter: "blur(12px)",
            borderBottom: scrolled
              ? "1px solid rgba(200,169,107,0.3)"
              : "1px solid rgba(255,255,255,0.08)",
            transition: "all 0.3s ease",
            boxShadow: scrolled ? "0 4px 30px rgba(0,0,0,0.3)" : "none",
          }}
        >
          <Container maxWidth="xl">
            <Toolbar
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                py: scrolled ? 0.5 : 1,
                transition: "all 0.3s ease",
                minHeight: { xs: "70px", md: "80px" },
              }}
            >
              {/* Logo Section */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, ease: "easeOut" }}
              >
                <Box
                  component={Link}
                  to="/"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    textDecoration: "none",
                    position: "relative",
                  }}
                >
                  {/* Animated ring around logo on hover */}
                  <Box sx={{ position: "relative" }}>
                    <Avatar
                      src="/images/logo.png"
                      alt="John Asfar Logo"
                      sx={{
                        width: { xs: 45, md: 55 },
                        height: { xs: 45, md: 55 },
                        border: "2px solid #C8A96B",
                        transition: "all 0.3s ease",
                        cursor: "pointer",
                        "&:hover": {
                          transform: "scale(1.05) rotate(5deg)",
                          boxShadow: "0 0 20px rgba(200,169,107,0.5)",
                        },
                      }}
                    />
                    <Box
                      sx={{
                        position: "absolute",
                        top: -2,
                        left: -2,
                        right: -2,
                        bottom: -2,
                        borderRadius: "50%",
                        border: "1px solid rgba(200,169,107,0.3)",
                        animation: "pulse 2s infinite",
                        "@keyframes pulse": {
                          "0%": {
                            transform: "scale(1)",
                            opacity: 0.6,
                          },
                          "100%": {
                            transform: "scale(1.2)",
                            opacity: 0,
                          },
                        },
                      }}
                    />
                  </Box>
                  <Box>
                    <Typography
                      variant="h6"
                      sx={{
                        color: "#C8A96B",
                        fontWeight: 800,
                        letterSpacing: { xs: 2, md: 4 },
                        fontSize: { xs: "1rem", md: "1.3rem" },
                        lineHeight: 1.2,
                        background: scrolled 
                          ? "linear-gradient(135deg, #C8A96B, #E8D5A8)"
                          : "none",
                        backgroundClip: scrolled ? "text" : "none",
                        WebkitBackgroundClip: scrolled ? "text" : "none",
                        color: scrolled ? "transparent" : "#C8A96B",
                        transition: "all 0.3s ease",
                      }}
                    >
                      JOHN ASFAR
                    </Typography>
                    <Typography
                      sx={{
                        color: "rgba(200,169,107,0.7)",
                        fontSize: { xs: "0.6rem", md: "0.7rem" },
                        letterSpacing: 1.5,
                        display: { xs: "none", sm: "block" },
                        fontWeight: 500,
                      }}
                    >
                      EXECUTIVE LEADERSHIP
                    </Typography>
                  </Box>
                </Box>
              </motion.div>

              {/* Desktop Navigation */}
              <Box sx={{ display: { xs: "none", md: "flex" }, gap: 0.5 }}>
                {navLinks.map((link, index) => (
                  <motion.div
                    key={link.title}
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.08 }}
                  >
                    <Tooltip 
                      title={link.title} 
                      placement="bottom"
                      arrow
                      sx={{ 
                        '& .MuiTooltip-tooltip': { 
                          background: '#C8A96B', 
                          color: '#000',
                          fontWeight: 600,
                        } 
                      }}
                    >
                      <Button
                        component={Link}
                        to={link.path}
                        onMouseEnter={() => setHoveredLink(link.title)}
                        onMouseLeave={() => setHoveredLink(null)}
                        sx={{
                          color: isActive(link.path) ? "#C8A96B" : "#fff",
                          fontWeight: isActive(link.path) ? 700 : 500,
                          position: "relative",
                          px: 2.5,
                          py: 1,
                          mx: 0.5,
                          borderRadius: "8px",
                          fontSize: "0.95rem",
                          letterSpacing: "0.5px",
                          overflow: "hidden",
                          "&::before": {
                            content: '""',
                            position: "absolute",
                            bottom: 0,
                            left: "50%",
                            transform: "translateX(-50%)",
                            width: isActive(link.path) ? "35px" : "0",
                            height: "2px",
                            background: "linear-gradient(90deg, #C8A96B, #E8D5A8)",
                            transition: "width 0.3s ease",
                          },
                          "&::after": {
                            content: '""',
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%) scale(0)",
                            width: "100%",
                            height: "100%",
                            background: "radial-gradient(circle, rgba(200,169,107,0.1), transparent)",
                            borderRadius: "8px",
                            transition: "transform 0.4s ease",
                            zIndex: -1,
                          },
                          "&:hover": {
                            color: "#C8A96B",
                            background: "transparent",
                            "&::before": {
                              width: "35px",
                            },
                            "&::after": {
                              transform: "translate(-50%, -50%) scale(1)",
                            },
                          },
                        }}
                      >
                        {link.title}
                      </Button>
                    </Tooltip>
                  </motion.div>
                ))}
              </Box>

              {/* Mobile Menu Button */}
              <IconButton
                onClick={handleDrawerToggle}
                sx={{
                  display: { xs: "flex", md: "none" },
                  color: "#fff",
                  background: scrolled ? "rgba(200,169,107,0.1)" : "transparent",
                  "&:hover": {
                    color: "#C8A96B",
                    background: "rgba(200,169,107,0.2)",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                <AnimatePresence mode="wait">
                  <motion.div
                    key={mobileOpen ? "close" : "menu"}
                    initial={{ rotate: -90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: 90, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    {mobileOpen ? (
                      <CloseIcon sx={{ fontSize: 28 }} />
                    ) : (
                      <MenuIcon sx={{ fontSize: 28 }} />
                    )}
                  </motion.div>
                </AnimatePresence>
              </IconButton>
            </Toolbar>
          </Container>
        </AppBar>
      </HideOnScroll>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        PaperProps={{
          sx: {
            width: 320,
            background: "linear-gradient(135deg, #0B0B0B, #111111)",
            borderLeft: "1px solid rgba(200,169,107,0.2)",
            boxShadow: "-10px 0 30px rgba(0,0,0,0.5)",
          },
        }}
      >
        {drawer}
      </Drawer>
    </>
  );
};

export default Navbar;